import React from 'react';
import { motion } from 'framer-motion';
import { HUMANS, scenarios } from '../../data/populations';
import { useScenario } from '../../contexts/ScenarioContext';
import { formatRatio } from '../../utils/format';
import { SourceRef } from '../SourceRef';
import { Link } from 'react-router-dom';
import { allParity, yearLabel } from '../../utils/parity';
const deviceParity = allParity.find(p => p.scenario.id === 'device') ?? allParity[1];
const TICKS = [0.001, 0.01, 0.1, 1, 10];
const toPct = (r: number) => Math.min(100, Math.max(0, (Math.log10(r) + 3) / 4 * 100));
export function RatioGauge() {
  const {
    scenario
  } = useScenario();
  const ratio = scenario.count / HUMANS.base;
  const lowPct = toPct(scenario.low / HUMANS.base);
  const highPct = toPct(scenario.high / HUMANS.base);
  return <section aria-labelledby="gauge-title" className="pt-10" data-id="element-134" data-mp="2solbpfbn4d0b">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-135" data-mp="2nqjrus6e49ns">
        <div data-id="element-136" data-mp="2nqa3ta4wockq">
          <h2 id="gauge-title" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted" data-id="element-137" data-mp="2nsw7gyooe7xo">
            Agents per human
          </h2>
          <p className="mt-2 font-mono text-4xl tabular text-machine md:text-5xl" data-id="element-138" data-mp="w7n2m4blhcbi">
            {formatRatio(ratio)} <span className="text-muted" data-id="element-139" data-mp="118strxplwrix">: 1</span>
            <SourceRef id="fermi" estimate={{
            low: formatRatio(scenario.low / HUMANS.base),
            high: formatRatio(scenario.high / HUMANS.base)
          }} data-id="element-140" data-mp="166ueen2okywi" />
          </p>
        </div>
        <p className="max-w-xs text-right text-[13px] leading-snug text-muted" data-id="element-141" data-mp="8kx3baqmrvul">
          Parity projected 2026–2027 (base case, device range high)
          <SourceRef id="fermi" estimate={{
          low: yearLabel(deviceParity.high),
          high: yearLabel(deviceParity.central)
        }} note="High bound vs central linear estimate." data-id="element-142" data-mp="igrq8i1w0d36" />
          <Link to="/parity" className="mt-1 block text-[12px] text-ink underline decoration-line underline-offset-2 hover:text-machine" data-id="element-143" data-mp="n9t88yxe4sqz">
            See the math and countdown →
          </Link>
        </p>
      </div>

      <div className="relative mt-8 pb-8" role="img" aria-label={`Log scale gauge. Current scenario ${formatRatio(ratio)} agents per human; parity at 1.0.`} data-id="element-144" data-mp="3iw5fojs8rjy9">
        <div className="relative h-3 rounded-full bg-raised" data-id="element-145" data-mp="3euju8a1n5xcg">
          <div className="absolute inset-y-0 rounded-full border border-machine/40 bg-machine/10" style={{
          left: `${lowPct}%`,
          width: `${Math.max(0.6, highPct - lowPct)}%`
        }} data-id="element-146" data-mp="3smgl6arkq179" />
          <motion.div className="absolute inset-y-0 left-0 rounded-full bg-machine" initial={false} animate={{
          width: `${toPct(ratio)}%`
        }} transition={{
          duration: 0.28,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-147" data-mp="2zx9lh8gdv5kf" />
          {scenarios.filter(s => s.id !== scenario.id).map(s => <span key={s.id} className="absolute top-1/2 h-2 w-px -translate-y-1/2 bg-ink/40" style={{
          left: `${toPct(s.count / HUMANS.base)}%`
        }} aria-hidden="true" data-id="element-148" data-mp="34vb3dq22oyub" />)}
        </div>
        <div className="absolute -top-3 bottom-4 w-px bg-human" style={{
        left: `${toPct(1)}%`
      }} aria-hidden="true" data-id="element-149" data-mp="k5vl5jgx6iga">
          <span className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[11px] text-human" data-id="element-150" data-mp="2264mjq25rpyf">parity = 1.0</span>
        </div>
        <div className="absolute inset-x-0 bottom-0 flex justify-between font-mono text-[10.5px] text-muted" aria-hidden="true" data-id="element-151" data-mp="1t4dh8mbj5l53">
          {TICKS.map(t => <span key={t} className="tabular" data-id="element-152" data-mp="2lrzhplpgo9nq">
              {t}
            </span>)}
        </div>
      </div>
    </section>;
}